import React, { useState, useMemo, useCallback } from "react";
import { useProcurementUser } from "../context/ProcurementUserContext";
import ProcurementUserSwitcher from "../components/ProcurementUserSwitcher";

const API = process.env.REACT_APP_API_URL || "";
const API_KEY = process.env.REACT_APP_API_KEY || "";

const C = {
  bg: "#0F1117", card: "#161C2E", cardBorder: "#222A40",
  gold: "#C9972B", text: "#F4F0E8", textSec: "#9EA8C0", textDim: "#5C657A",
  red: "#EF4444", green: "#22C55E", amber: "#F59E0B", blue: "#3B82F6",
  teal: "#14B8A6", orange: "#F97316", violet: "#8B5CF6", rule: "#2A3048",
};

const STATUS_COLORS = {
  pending: "#64748B", submitted: C.blue, under_review: C.amber,
  approved: C.green, approved_as_noted: C.teal,
  revise_resubmit: C.orange, rejected: C.red, void: "#6B7280",
};
const GC_COLORS = { Procore: C.blue, Helix: C.violet, Other: "#64748B" };
const ALL_STATUSES = Object.keys(STATUS_COLORS);
const NEXT_STEPS = {
  pending: "Next: Submit to GC", submitted: "Next: Await GC response",
  under_review: "Next: Await approval or comments",
  approved: "\u2713 No further action required",
  approved_as_noted: "\u2713 Confirm any noted conditions",
  revise_resubmit: "\u26A0 Revise and resubmit to GC",
  rejected: "\u2717 Coordinate with Engineering",
  void: "Submittal voided",
};
const PAGE_SIZE = 50;

function Badge({ text, color }) {
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: "monospace", background: `${color}22`, color, border: `1px solid ${color}44` }}>{text?.replace(/_/g, " ")}</span>;
}
function Toast({ msg, onClose }) {
  if (!msg) return null;
  return <div style={{ position: "fixed", bottom: 24, right: 24, background: C.green, color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontFamily: "monospace", zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>{msg}<button onClick={onClose} style={{ marginLeft: 12, background: "none", border: "none", color: "#fff", cursor: "pointer", fontWeight: 700 }}>x</button></div>;
}

function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.round((new Date(b) - new Date(a)) / 86400000);
}

export default function ProjectDocumentation() {
  const { activeUser } = useProcurementUser();
  const hdrs = { "Content-Type": "application/json", ...(API_KEY ? { "X-API-Key": API_KEY } : {}), "X-User": activeUser.identifier };

  const [allocation, setAllocation] = useState("");
  const [submittals, setSubmittals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [filters, setFilters] = useState({ vendor: "", equip: "", gc: "" });
  const [sortCol, setSortCol] = useState("submitted_date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);

  // Add modal
  const [aNum, setANum] = useState(""); const [aEquip, setAEquip] = useState(""); const [aVendor, setAVendor] = useState("");
  const [aGc, setAGc] = useState("Procore"); const [aRef, setARef] = useState(""); const [aStatus, setAStatus] = useState("pending");
  const [aDate, setADate] = useState(""); const [aSp, setASp] = useState(""); const [aNotes, setANotes] = useState(""); const [aErr, setAErr] = useState(null);

  // Edit modal
  const [eStatus, setEStatus] = useState(""); const [eResp, setEResp] = useState(""); const [eRev, setERev] = useState("");
  const [eSp, setESp] = useState(""); const [eNotes, setENotes] = useState(""); const [eErr, setEErr] = useState(null);

  const loadSubmittals = useCallback(async () => {
    if (!allocation.trim()) return;
    setLoading(true); setError(null); setSubmittals(null); setPage(0); setStatusFilter(null);
    try {
      const r = await fetch(`${API}/procurement/submittals/${encodeURIComponent(allocation.trim())}`, { headers: hdrs });
      const d = await r.json();
      setSubmittals(d.submittals || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [allocation]);

  const createSubmittal = async () => {
    if (!allocation.trim()) { setToast("Load a project allocation first"); setTimeout(() => setToast(null), 3000); return; }
    setAErr(null);
    try {
      await fetch(`${API}/procurement/submittals`, { method: "POST", headers: hdrs, body: JSON.stringify({
        allocation: allocation.trim(), submittal_number: aNum || null, equipment_type: aEquip || null,
        gc_system: aGc || null, gc_submittal_ref: aRef || null, status: aStatus,
        submitted_date: aDate || null, sharepoint_link: aSp || null, notes: aNotes || null,
      }) });
      setAddOpen(false); setANum(""); setAEquip(""); setAVendor(""); setAGc("Procore"); setARef(""); setAStatus("pending"); setADate(""); setASp(""); setANotes("");
      setToast("Submittal added"); loadSubmittals(); setTimeout(() => setToast(null), 3000);
    } catch (e) { setAErr(e.message); }
  };

  const openEdit = (item) => {
    setEditItem(item); setEStatus(item.status || "pending"); setEResp(item.responded_date || "");
    setERev(item.latest_revision || ""); setESp(item.sharepoint_link || ""); setENotes(item.notes || ""); setEErr(null);
  };

  const updateSubmittal = async () => {
    setEErr(null);
    const body = {};
    if (eStatus && eStatus !== editItem.status) body.status = eStatus;
    if (eResp) body.responded_date = eResp;
    if (eRev) body.latest_revision = eRev;
    if (eSp) body.sharepoint_link = eSp;
    if (eNotes && eNotes !== editItem.notes) body.notes = eNotes;
    if (Object.keys(body).length === 0) { setEditItem(null); return; }
    try {
      await fetch(`${API}/procurement/submittals/${editItem.id}`, { method: "PATCH", headers: hdrs, body: JSON.stringify(body) });
      setEditItem(null); setToast(`Submittal updated \u2192 ${body.status || editItem.status}`); loadSubmittals(); setTimeout(() => setToast(null), 3000);
    } catch (e) { setEErr(e.message); }
  };

  const handleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } setPage(0); };

  const vendorNames = useMemo(() => [...new Set((submittals || []).map(r => r.vendor_name).filter(Boolean))].sort(), [submittals]);
  const statusCounts = useMemo(() => {
    const c = {};
    for (const s of (submittals || [])) { c[s.status] = (c[s.status] || 0) + 1; }
    return c;
  }, [submittals]);

  const filteredRows = useMemo(() => {
    let rows = submittals || [];
    if (statusFilter) rows = rows.filter(r => r.status === statusFilter);
    if (filters.vendor) rows = rows.filter(r => r.vendor_name === filters.vendor);
    if (filters.equip) rows = rows.filter(r => (r.equipment_type || "").toLowerCase().includes(filters.equip.toLowerCase()));
    if (filters.gc) rows = rows.filter(r => r.gc_system === filters.gc);
    return rows;
  }, [submittals, statusFilter, filters]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      let av = a[sortCol], bv = b[sortCol];
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === "string" && typeof bv === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av - bv) : (bv - av);
    });
  }, [filteredRows, sortCol, sortDir]);

  const pagedRows = useMemo(() => sortedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [sortedRows, page]);
  const totalPages = Math.ceil(sortedRows.length / PAGE_SIZE);

  const si = (col) => sortCol === col ? (sortDir === "asc" ? " \u2191" : " \u2193") : " \u2195";
  const th = { padding: "8px 10px", fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", color: C.textDim, cursor: "pointer", whiteSpace: "nowrap", borderBottom: `1px solid ${C.rule}`, textAlign: "left", letterSpacing: "0.06em" };
  const td = { padding: "6px 10px", fontSize: 12, borderBottom: `1px solid ${C.rule}`, fontFamily: "monospace" };
  const inp = { background: C.bg, border: `1px solid ${C.cardBorder}`, color: C.text, borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "monospace", outline: "none" };
  const sel = { ...inp, cursor: "pointer" };
  const btn = { background: C.gold, color: C.bg, border: "none", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "monospace" };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Outfit', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "16px 24px 48px" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, borderBottom: `1px solid ${C.rule}`, paddingBottom: 12 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Project Documentation</h1>
            {submittals && <div style={{ fontSize: 12, color: C.textSec }}>{submittals.length} submittals | {allocation}</div>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <ProcurementUserSwitcher />
            <button onClick={() => { if (!allocation.trim()) { setToast("Load a project allocation first"); setTimeout(() => setToast(null), 3000); return; } setAddOpen(true); setAErr(null); }} style={btn}>+ Add Submittal</button>
            <button onClick={loadSubmittals} style={{ ...btn, background: C.card, color: C.textSec, border: `1px solid ${C.cardBorder}` }}>Refresh</button>
          </div>
        </div>

        {/* OWNERSHIP BANNER */}
        <div style={{ background: `${C.rule}44`, borderRadius: 8, padding: "8px 16px", marginBottom: 12, fontSize: 12, color: C.textSec }}>
          <span style={{ marginRight: 6 }}>&#x1F4C1;</span>
          ADC-owned submittal register. This is the source of record — independent of GC-controlled Procore or Helix access. All submittals are filed in SharePoint.
        </div>

        {/* ALLOCATION SELECTOR */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
          <input value={allocation} onChange={e => setAllocation(e.target.value)} placeholder="Enter allocation (e.g. DEC3.0-PHX-B1)" onKeyDown={e => e.key === "Enter" && loadSubmittals()}
            style={{ ...inp, flex: 1, maxWidth: 400 }} />
          <button onClick={loadSubmittals} disabled={loading || !allocation.trim()} style={btn}>{loading ? "Loading..." : "Load"}</button>
        </div>

        {error && <div style={{ color: C.red, marginBottom: 12, fontSize: 13 }}>{error}</div>}

        {/* NOT LOADED */}
        {!submittals && !loading && !error && (
          <div style={{ textAlign: "center", padding: "80px 0", color: C.textDim }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#x1F4C4;</div>
            <div style={{ fontSize: 15 }}>Enter a project allocation to load the submittal register.</div>
          </div>
        )}

        {/* EMPTY AFTER LOAD */}
        {submittals && submittals.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: C.textDim }}>
            <div style={{ fontSize: 15 }}>No submittals found for <strong style={{ color: C.gold }}>{allocation}</strong>.</div>
            <div style={{ fontSize: 12, marginTop: 6, marginBottom: 12 }}>Use + Add Submittal to begin tracking.</div>
            <button onClick={() => setAddOpen(true)} style={btn}>+ Add Submittal</button>
          </div>
        )}

        {/* LOADED */}
        {submittals && submittals.length > 0 && (
          <>
            {/* STATUS SUMMARY */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={() => { setStatusFilter(null); setPage(0); }} style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontFamily: "monospace", cursor: "pointer", background: !statusFilter ? C.gold : C.card, color: !statusFilter ? C.bg : C.textSec, border: !statusFilter ? "none" : `1px solid ${C.cardBorder}`, fontWeight: 600 }}>All ({submittals.length})</button>
              {ALL_STATUSES.filter(s => statusCounts[s]).map(s => (
                <button key={s} onClick={() => { setStatusFilter(statusFilter === s ? null : s); setPage(0); }}
                  style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11, fontFamily: "monospace", cursor: "pointer",
                    background: statusFilter === s ? `${STATUS_COLORS[s]}33` : C.card,
                    color: STATUS_COLORS[s], border: statusFilter === s ? `1px solid ${STATUS_COLORS[s]}` : `1px solid ${C.cardBorder}`, fontWeight: 600 }}>
                  {s.replace(/_/g, " ")} ({statusCounts[s]})
                </button>
              ))}
            </div>

            {/* FILTERS */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
              <select value={filters.vendor} onChange={e => { setFilters(f => ({ ...f, vendor: e.target.value })); setPage(0); }} style={sel}>
                <option value="">All Vendors</option>
                {vendorNames.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
              <input value={filters.equip} onChange={e => { setFilters(f => ({ ...f, equip: e.target.value })); setPage(0); }} placeholder="Equipment Type" style={{ ...inp, width: 140 }} />
              <select value={filters.gc} onChange={e => { setFilters(f => ({ ...f, gc: e.target.value })); setPage(0); }} style={sel}>
                <option value="">All GC Systems</option>
                <option value="Procore">Procore</option>
                <option value="Helix">Helix</option>
                <option value="Other">Other</option>
              </select>
              <button onClick={() => { setFilters({ vendor: "", equip: "", gc: "" }); setStatusFilter(null); setPage(0); }} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Clear</button>
              <span style={{ fontSize: 11, color: C.textDim, fontFamily: "monospace" }}>{sortedRows.length} rows | Page {page + 1}/{totalPages || 1}</span>
            </div>

            {/* TABLE */}
            <div style={{ overflowX: "auto", borderRadius: 12, border: `1px solid ${C.cardBorder}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: C.card }}>
                <thead>
                  <tr>
                    <th style={th}>Status</th>
                    <th style={th}>Sub #</th>
                    <th style={th} onClick={() => handleSort("equipment_type")}>Equip{si("equipment_type")}</th>
                    <th style={th} onClick={() => handleSort("vendor_name")}>Vendor{si("vendor_name")}</th>
                    <th style={th}>GC Sys</th>
                    <th style={th}>GC Ref</th>
                    <th style={th} onClick={() => handleSort("submitted_date")}>Submitted{si("submitted_date")}</th>
                    <th style={th} onClick={() => handleSort("responded_date")}>Responded{si("responded_date")}</th>
                    <th style={th}>Rev</th>
                    <th style={th}>SharePoint</th>
                    <th style={th}>Notes</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((r, i) => {
                    const isVoid = r.status === "void";
                    const bl = r.status === "revise_resubmit" ? `3px solid ${C.orange}` : r.status === "rejected" ? `3px solid ${C.red}` : ["approved","approved_as_noted"].includes(r.status) ? `3px solid ${C.green}` : "3px solid transparent";
                    const gap = daysBetween(r.submitted_date, r.responded_date);
                    const respColor = gap !== null ? (gap <= 14 ? C.green : gap > 21 ? C.red : C.text) : C.text;
                    return (
                      <tr key={r.id || i} style={{ borderLeft: bl, opacity: isVoid ? 0.5 : 1 }}>
                        <td style={td}><Badge text={r.status} color={STATUS_COLORS[r.status] || "#64748B"} /></td>
                        <td style={{ ...td, color: C.amber }}>{isVoid ? <s>{r.submittal_number}</s> : r.submittal_number || "—"}</td>
                        <td style={td}>{r.equipment_type || "—"}</td>
                        <td style={{ ...td, color: C.textSec }}>{r.vendor_name || "—"}</td>
                        <td style={td}>{r.gc_system ? <Badge text={r.gc_system} color={GC_COLORS[r.gc_system] || "#64748B"} /> : "—"}</td>
                        <td style={{ ...td, color: C.textDim }}>{r.gc_submittal_ref || "—"}</td>
                        <td style={td}>{r.submitted_date || "—"}</td>
                        <td style={{ ...td, color: respColor }}>{r.responded_date || "—"}</td>
                        <td style={{ ...td, color: r.latest_revision ? C.amber : C.textDim }}>{r.latest_revision || "—"}</td>
                        <td style={td}>{r.sharepoint_link ? <a href={r.sharepoint_link} target="_blank" rel="noreferrer" style={{ color: C.blue, textDecoration: "none", fontSize: 11 }}>Open &#x2197;</a> : "—"}</td>
                        <td style={{ ...td, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.notes}>{r.notes?.slice(0, 30) || ""}</td>
                        <td style={td}><button onClick={() => openEdit(r)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 4, padding: "2px 8px", fontSize: 10, cursor: "pointer", fontFamily: "monospace" }}>Update</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12 }}>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>&laquo; Prev</button>
                <span style={{ color: C.textDim, fontSize: 12, padding: "4px 8px", fontFamily: "monospace" }}>{page + 1} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, color: C.textSec, borderRadius: 6, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}>Next &raquo;</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ADD MODAL */}
      {addOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setAddOpen(false)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 480, maxWidth: "90vw", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Add Submittal</div>
            {[["Submittal Number", aNum, setANum], ["Equipment Type", aEquip, setAEquip], ["Vendor", aVendor, setAVendor], ["GC Reference #", aRef, setARef], ["SharePoint Link", aSp, setASp]].map(([l, v, s]) => (
              <div key={l} style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>{l}</label><input value={v} onChange={e => s(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            ))}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>GC System</label><select value={aGc} onChange={e => setAGc(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}><option>Procore</option><option>Helix</option><option>Other</option></select></div>
              <div style={{ flex: 1 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Status</label><select value={aStatus} onChange={e => setAStatus(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>{ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>
            </div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Submitted Date</label><input type="date" value={aDate} onChange={e => setADate(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Notes</label><textarea value={aNotes} onChange={e => setANotes(e.target.value)} rows={2} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} /></div>
            {aErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{aErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setAddOpen(false)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={createSubmittal} style={btn}>Add Submittal</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900 }} onClick={() => setEditItem(null)}>
          <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, width: 440, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Update</div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16, fontFamily: "monospace" }}>{editItem.submittal_number || editItem.equipment_type || `#${editItem.id}`}</div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Status</label>
              <select value={eStatus} onChange={e => setEStatus(e.target.value)} style={{ ...sel, width: "100%", boxSizing: "border-box" }}>{ALL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select>
              <div style={{ fontSize: 11, color: STATUS_COLORS[eStatus] || C.textDim, marginTop: 4, fontStyle: "italic" }}>{NEXT_STEPS[eStatus] || ""}</div>
            </div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Responded Date</label><input type="date" value={eResp} onChange={e => setEResp(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Latest Revision</label><input value={eRev} onChange={e => setERev(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>SharePoint Link</label><input value={eSp} onChange={e => setESp(e.target.value)} style={{ ...inp, width: "100%", boxSizing: "border-box" }} /></div>
            <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, color: C.textDim, display: "block", marginBottom: 2 }}>Notes</label><textarea value={eNotes} onChange={e => setENotes(e.target.value)} rows={2} style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical" }} /></div>
            {eErr && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{eErr}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setEditItem(null)} style={{ background: "none", border: `1px solid ${C.textDim}`, color: C.textSec, borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
              <button onClick={updateSubmittal} style={btn}>Save</button>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast} onClose={() => setToast(null)} />
    </div>
  );
}
